import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, DownloadSimple, UploadSimple, FileXls, Plus } from "@phosphor-icons/react";
import { listProducts, getCategories, exportProductsUrl, templateProductsUrl, importProducts, consumptionChart } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import ProductPicker from "@/components/ProductPicker";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { toast } from "sonner";

export default function Stock() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [picker, setPicker] = useState(false);
  const [selected, setSelected] = useState(null);
  const [chart, setChart] = useState([]);
  const fileRef = useRef();

  const load = async () => {
    const [p, c] = await Promise.all([listProducts({ q, category: cat || undefined }), getCategories()]);
    setProducts(p);
    setCats(c);
  };
  useEffect(() => { load(); }, [q, cat]); // eslint-disable-line

  useEffect(() => {
    if (selected) {
      consumptionChart({ product_id: selected.id, days: 30 }).then(setChart);
    }
  }, [selected]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return products;
    if (statusFilter === "alerts") return products.filter((p) => ["low", "critical", "rupture"].includes(p.status));
    return products.filter((p) => p.status === statusFilter);
  }, [products, statusFilter]);

  const counts = useMemo(() => ({
    all: products.length,
    ok: products.filter((p) => p.status === "ok").length,
    low: products.filter((p) => p.status === "low").length,
    critical: products.filter((p) => p.status === "critical").length,
    rupture: products.filter((p) => p.status === "rupture").length,
  }), [products]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await importProducts(file);
      toast.success(`Import : ${r.created} créés · ${r.updated} mis à jour`);
      if (r.errors?.length) toast.warning(`${r.errors.length} erreur(s)`, { description: r.errors.slice(0, 3).join("\n") });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec import");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="fade-up space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-[color:var(--text-muted)]">Consultation</div>
          <h1 className="text-4xl font-heading font-medium mt-1">État du stock</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={templateProductsUrl()} className="h-12 px-4 rounded-2xl border border-[color:var(--border-soft)] flex items-center gap-2 bg-white press-scale hover:bg-[color:var(--bg)] text-sm font-medium" data-testid="btn-template">
            <FileXls size={20} /> Modèle Excel
          </a>
          <a href={exportProductsUrl()} className="h-12 px-4 rounded-2xl border border-[color:var(--border-soft)] flex items-center gap-2 bg-white press-scale hover:bg-[color:var(--bg)] text-sm font-medium" data-testid="btn-export">
            <DownloadSimple size={20} /> Exporter
          </a>
          <button onClick={() => fileRef.current?.click()} className="h-12 px-4 rounded-2xl border border-[color:var(--border-soft)] flex items-center gap-2 bg-white press-scale hover:bg-[color:var(--bg)] text-sm font-medium" data-testid="btn-import">
            <UploadSimple size={20} /> Importer
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={handleImport} />
          <button onClick={() => setPicker(true)} className="h-12 px-4 rounded-2xl bg-[color:var(--brand)] text-white flex items-center gap-2 press-scale hover:bg-[color:var(--brand-hover)] text-sm font-medium" data-testid="btn-add-product">
            <Plus size={20} weight="bold" /> Produit
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="status-filters">
        <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label={`Tous (${counts.all})`} testid="filter-all" />
        <FilterChip active={statusFilter === "alerts"} onClick={() => setStatusFilter("alerts")} label={`Alertes (${counts.critical + counts.rupture + counts.low})`} testid="filter-alerts" />
        <FilterChip active={statusFilter === "rupture"} onClick={() => setStatusFilter("rupture")} label={`Rupture (${counts.rupture})`} tone="rupture" testid="filter-rupture" />
        <FilterChip active={statusFilter === "critical"} onClick={() => setStatusFilter("critical")} label={`Critique (${counts.critical})`} tone="critical" testid="filter-critical" />
        <FilterChip active={statusFilter === "low"} onClick={() => setStatusFilter("low")} label={`Faible (${counts.low})`} tone="low" testid="filter-low" />
        <FilterChip active={statusFilter === "ok"} onClick={() => setStatusFilter("ok")} label={`OK (${counts.ok})`} tone="ok" testid="filter-ok" />
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
          <input
            data-testid="search-stock"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un produit…"
            className="w-full h-12 pl-11 pr-4 rounded-2xl border border-[color:var(--border-soft)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
          />
        </div>
        <select data-testid="cat-filter" value={cat} onChange={(e) => setCat(e.target.value)} className="h-12 px-4 rounded-2xl border border-[color:var(--border-soft)] bg-white focus:outline-none min-w-[180px]">
          <option value="">Toutes catégories</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-3xl border border-[color:var(--border-soft)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stock-table">
            <thead className="bg-[color:var(--brand-muted)]">
              <tr className="text-left">
                <Th>Produit</Th>
                <Th>Catégorie</Th>
                <Th>Stock</Th>
                <Th>Seuil</Th>
                <Th>Prix U.</Th>
                <Th>Fournisseur</Th>
                <Th>Statut</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-14 text-[color:var(--text-muted)]">Aucun produit dans cette vue.</td></tr>
              )}
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-[color:var(--border-soft)] cursor-pointer hover:bg-[color:var(--bg)]"
                  onClick={() => setSelected(p)}
                  data-testid={`row-${p.id}`}
                >
                  <td className="py-4 px-4 font-medium">{p.name}</td>
                  <td className="py-4 px-4 text-[color:var(--text-secondary)]">{p.category}</td>
                  <td className="py-4 px-4 font-semibold tabular-nums">{p.stock} <span className="text-xs text-[color:var(--text-muted)] font-normal">{p.unit}</span></td>
                  <td className="py-4 px-4 tabular-nums text-[color:var(--text-muted)]">{p.min_threshold}</td>
                  <td className="py-4 px-4 tabular-nums">{p.unit_price?.toFixed(2)} €</td>
                  <td className="py-4 px-4 text-[color:var(--text-secondary)]">{p.supplier_name || "—"}</td>
                  <td className="py-4 px-4"><StatusPill status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DetailPanel product={selected} chart={chart} onClose={() => setSelected(null)} onUpdated={load} />
      )}
      {picker && <ProductPicker onClose={() => { setPicker(false); load(); }} onPick={() => { setPicker(false); load(); }} />}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="py-3 px-4 text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">{children}</th>
);

function FilterChip({ active, onClick, label, tone, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`h-11 px-4 rounded-full text-sm font-medium press-scale border transition-colors ${
        active
          ? "bg-[color:var(--brand)] text-white border-[color:var(--brand)]"
          : `bg-white border-[color:var(--border-soft)] text-[color:var(--text-secondary)] hover:border-[color:var(--brand)]`
      }`}
    >
      {label}
    </button>
  );
}

function DetailPanel({ product, chart, onClose, onUpdated }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose} data-testid="detail-panel">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[color:var(--border-soft)] flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">{product.category}</div>
            <h2 className="font-heading text-2xl font-medium mt-1">{product.name}</h2>
          </div>
          <StatusPill status={product.status} size="lg" />
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Metric label="Stock actuel" value={`${product.stock} ${product.unit}`} big />
            <Metric label="Seuil minimum" value={product.min_threshold} />
            <Metric label="Prix unitaire" value={`${product.unit_price?.toFixed(2)} €`} />
            <Metric label="Conditionnement" value={product.pack_size} />
            <Metric label="Fournisseur" value={product.supplier_name || "—"} wide />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)] mb-2">Consommation 30j</div>
            <div className="h-44 bg-[color:var(--bg)] rounded-2xl p-3">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart}>
                  <XAxis dataKey="date" hide />
                  <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString("fr-FR")} formatter={(v) => [v, "sorties"]} contentStyle={{ borderRadius: 12 }} />
                  <Line type="monotone" dataKey="value" stroke="#2A5A4A" strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          {product.notes && (
            <div>
              <div className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)] mb-1">Notes</div>
              <div className="text-sm text-[color:var(--text-secondary)] bg-[color:var(--bg)] p-3 rounded-xl">{product.notes}</div>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-[color:var(--border-soft)] flex justify-end">
          <button onClick={onClose} className="h-12 px-5 rounded-2xl border border-[color:var(--border-soft)]" data-testid="detail-close">Fermer</button>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, big, wide }) {
  return (
    <div className={`${wide ? "col-span-2" : ""}`}>
      <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">{label}</div>
      <div className={`${big ? "text-3xl font-heading font-semibold" : "text-lg font-medium"} mt-0.5`}>{value}</div>
    </div>
  );
}
